import { useEffect, useCallback, useMemo } from 'react';
import { useLevelsStore } from '../store';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { useEntityCollectionInitializer } from 'store/entityCollection';
import { FormattedMessage } from 'intl';
import { translationKeyToString } from 'intl/types/translationKey';
import { DialogTranslationKeys } from 'intl/keys';

import { useProjectOrganizationId } from 'store/project';
import { keepUndeleted } from 'shared/domain/deletable/filters';
import { levelModelToLevelOnView } from 'shared/domain/level/mapping/toView';
import { LevelOnView } from 'shared/domain/level/types/view';
import { SiteModel } from 'shared/domain/site/types/model';
import {
  Deletable,
  HashMap,
  LabelledEntity,
} from 'shared/types/commonView';
import { useSites } from 'store/site';
import type { LevelModel } from 'shared/domain/level/types/model';
import type { LevelsToSelectSlice } from '../types';
import { ProjectSetupTranslationKeys } from 'intl/keys';
import { CommonTranslationKeys } from 'intl/keys';

export function useLevelsInitializer(): void {
  useEntityCollectionInitializer(useLevelsStore.use.actions());
}

function canDeleteLevel(levels: Deletable[]): {
  disabled: boolean;
  reason?: string;
} {
  const deleteDisabled = levels.filter(keepUndeleted).length <= 1;
  return {
    disabled: deleteDisabled,
    reason: ProjectSetupTranslationKeys.archiveDisabledReasonLastItem,
  };
}

export function useLevels(): {
  items: LevelModel[];
  levelsPerSite: HashMap<LevelOnView[]>;
  loading: boolean;
  /** True once a complete list arrived (also an empty one). */
  isReady: boolean;
  deleteLevel: (level: LabelledEntity) => void;
  restoreLevel: (level: LabelledEntity) => void;
  canDeleteLevel: (levels: Deletable[]) => {
    disabled: boolean;
    reason?: string;
  };
} {
  const organizationId = useProjectOrganizationId();
  const data = useLevelsStore.use.data();
  const isReady = useLevelsStore.use.isReady();
  const { archive, restore } = useLevelsStore.use.actions();
  const createDialog = useDialog();

  const levelsPerSite = useMemo(() => {
    return data.items.reduce<HashMap<LevelOnView[]>>(
      (result, level): HashMap<LevelOnView[]> => {
        if (Array.isArray(result[level.site])) {
          result[level.site].push(
            levelModelToLevelOnView(level, organizationId)
          );
        } else {
          result[level.site] = [
            levelModelToLevelOnView(level, organizationId),
          ];
        }
        return result;
      },
      {}
    );
  }, [data.items, organizationId]);

  const deleteLevel = useCallback(
    (level: LabelledEntity) => {
      createDialog({
        title: (
          <FormattedMessage id={DialogTranslationKeys.confirmationDeleteLevelTitle} />
        ),
        description: (
          <FormattedMessage id={DialogTranslationKeys.confirmationDeleteLevelDescription}
            values={{ levelName: level.label }}
          />
        ),
        customControllerLabels: [
          translationKeyToString(CommonTranslationKeys.cancel),
          translationKeyToString(CommonTranslationKeys.archive),
        ],
      }).then(() => archive(level._id));
    },
    [createDialog, archive]
  );

  const restoreLevel = useCallback(
    (level: LabelledEntity) => restore(level._id),
    [restore]
  );

  return {
    items: data.items,
    levelsPerSite,
    loading: data.loading,
    isReady,
    deleteLevel,
    restoreLevel,
    canDeleteLevel,
  };
}

function getLevelDeleted(
  site: SiteModel | undefined,
  level: LevelOnView | LevelModel
): boolean {
  if (!site) return true;
  return site.deleted || level.deleted;
}

/**
 * Derives levelsToSelect from levels + sites. Mounted globally in
 * WithEntityStoresInitializer (after useLevelsInitializer).
 */
export function useLevelsToSelectInitializer(): void {
  const { items: sites, loading: loadingSites } = useSites();
  const { items: levelsResponse, loading: loadingLevels } = useLevels();
  const organizationId = useProjectOrganizationId();
  const actions = useLevelsStore.use.actions();

  const sitesMap = useMemo(() => {
    const map = new Map<string, SiteModel>();
    sites.forEach((site) => map.set(site._id, site));
    return map;
  }, [sites]);

  const computed = useMemo((): Omit<LevelsToSelectSlice, 'loading'> => {
    const lvls: LevelModel[] = [];
    const lvlsPerSite = levelsResponse.reduce<HashMap<LevelOnView[]>>(
      (result, level): HashMap<LevelOnView[]> => {
        const site = sitesMap.get(level.site);
        const isDeleted = getLevelDeleted(site, level);
        const lvl = {
          ...level,
          deleted: isDeleted,
          siteLabel: site?.label ?? '',
        };

        if (Array.isArray(result[level.site])) {
          result[level.site].push(
            levelModelToLevelOnView(lvl, organizationId)
          );
        } else {
          result[level.site] = [
            levelModelToLevelOnView(lvl, organizationId),
          ];
        }
        lvls.push(lvl);
        return result;
      },
      {}
    );

    return { levels: lvls, levelsPerSite: lvlsPerSite };
  }, [sitesMap, levelsResponse, organizationId]);

  useEffect(() => {
    actions.setToSelect({
      levels: computed.levels,
      levelsPerSite: computed.levelsPerSite,
      loading: loadingSites || loadingLevels,
    });
  }, [computed, loadingSites, loadingLevels, actions]);
}

/**
 * Hook to get derived levels data for selection components.
 */
export function useLevelsToSelect(): LevelsToSelectSlice {
  const toSelect = useLevelsStore.use.toSelect();
  return toSelect;
}
